import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Button } from "../../components/ui/Button";
import { Helmet } from 'react-helmet-async';


type BlogPostSummary = {
  id: string;
  title: string;
  slug: string | null;
  is_featured: boolean;
  is_new: boolean;
  views: number | null;
  likes: number | null;
  published_at: string | null;
};

type MonthlyStat = {
  month: string; // ISO date string
  views: number;
};

export default function AdminDashboard() {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [monthlyStats, setMonthlyStats] = useState<MonthlyStat[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    // 1) Load posts for totals
    const { data, error } = await supabase
      .from("blog_posts")
      .select("id,title,slug,is_featured,is_new,views,likes,published_at")
      .order("published_at", { ascending: false });

    if (error) {
      console.error("Error loading dashboard stats:", error);
      setError(error.message);
      setLoading(false);
      return;
    }

    setPosts((data || []) as BlogPostSummary[]);

    // 2) Load monthly views from view
    const { data: monthlyData, error: monthlyError } = await supabase
      .from("blog_views_monthly")
      .select("*")
      .order("month", { ascending: false })
      .limit(6);

    if (monthlyError) {
      console.error("Error loading monthly stats:", monthlyError);
      // we don't block dashboard if this fails
    } else {
      setMonthlyStats(
        (monthlyData || []).map((row: any) => ({
          month: row.month,
          views: Number(row.views) || 0,
        }))
      );
    }

    setLoading(false);
  };

  const totalPosts = posts.length;
  const featuredCount = posts.filter((p) => p.is_featured).length;
  const newCount = posts.filter((p) => p.is_new).length;
  const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalLikes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);

  const recentPosts = posts.slice(0, 5);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  // for chart bar width
  const maxViews =
    monthlyStats.length > 0
      ? Math.max(...monthlyStats.map((m) => m.views))
      : 0;

  return (
    <>
    <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>

      <Header />
      <main className="min-h-screen px-4 py-8 max-w-7xl mx-auto space-y-8">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 border-b pb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Overview of your content, performance and leads.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/blog")}
            >
              Manage Blog Posts
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/leads")}
            >
              View Leads
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-600 border border-red-200 rounded-lg p-4 text-sm font-medium">
            ⚠️ {error}
          </div>
        )}

        {/* Stats cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Total Posts
            </p>
            <p className="text-2xl font-bold">{loading ? "…" : totalPosts}</p>
            <p className="text-xs text-muted-foreground">
              All published blog posts.
            </p>
          </div>

          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Featured Posts
            </p>
            <p className="text-2xl font-bold">
              {loading ? "…" : featuredCount}
            </p>
            <p className="text-xs text-muted-foreground">
              Posts marked as featured.
            </p>
          </div>

          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              New Posts
            </p>
            <p className="text-2xl font-bold">{loading ? "…" : newCount}</p>
            <p className="text-xs text-muted-foreground">
              Recently marked as NEW.
            </p>
          </div>

          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Total Views / Likes
            </p>
            <p className="text-xl font-bold">
              {loading ? "…" : `${totalViews} views`}
            </p>
            <p className="text-xs text-muted-foreground">
              {loading ? "" : `${totalLikes} total likes across posts.`}
            </p>
          </div>
        </section>

        {/* Monthly views "chart" */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Monthly Views</h2>
          {monthlyStats.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No view data yet. Once people start reading your articles, monthly
              stats will appear here.
            </p>
          ) : (
            <div className="border rounded-xl bg-card shadow-sm p-4 space-y-3 max-w-xl">
              {monthlyStats.map((row) => {
                const label = new Date(row.month).toLocaleDateString(
                  undefined,
                  { year: "numeric", month: "short" }
                );
                const percentage =
                  maxViews > 0 ? Math.round((row.views / maxViews) * 100) : 0;

                return (
                  <div key={row.month} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-text-primary">
                        {label}
                      </span>
                      <span className="text-muted-foreground">
                        {row.views} views
                      </span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${percentage || 5}%` }} // at least 5% visible
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent posts table */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Recent Posts</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/blog")}
            >
              Go to Posts Manager
            </Button>
          </div>

          <div className="border rounded-xl bg-card shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-6 text-sm text-muted-foreground">
                Loading recent posts...
              </div>
            ) : recentPosts.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">
                No posts yet. Click &ldquo;Manage Blog Posts&rdquo; to create
                your first one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Title
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">
                        Views
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">
                        Likes
                      </th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recentPosts.map((post) => (
                      <tr
                        key={post.id}
                        className="hover:bg-muted/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">
                            {post.title}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                            /{post.slug}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="flex gap-1">
                            {post.is_featured && (
                              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-[10px] font-bold">
                                FEAT
                              </span>
                            )}
                            {post.is_new && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-bold">
                                NEW
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-sm">
                          {post.views ?? 0}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-sm">
                          {post.likes ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate("/admin/blog")}
                          >
                            Edit in Manager
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
